import { useEffect, useState, useRef } from "react";
import { useApp } from "../context/AppContext";
import { formatProviderName, formatModelName } from "../utils/formatting";
import "../App.css"

function groupByProvider(modelBreakdown) {
  const providers = {};

  if (!modelBreakdown || !Array.isArray(modelBreakdown)) {
    return {};
  }

  modelBreakdown.forEach((model) => {
    const provider = model.provider || "Unknown";

    if (!providers[provider]) {
      providers[provider] = { cost: 0, credits: 0, requests: 0, tokens: 0 };
    }
    providers[provider].cost += parseFloat(model.cost);
    providers[provider].credits += Number(model.credits || 0);
    providers[provider].requests += model.requests;
    providers[provider].tokens += model.input_tokens + model.cached_tokens + model.output_tokens;
  });

  return providers;
}

function formatCost(cost) {
  if (typeof cost === "string") {
    cost = parseFloat(cost);
  }
  return `$${cost.toFixed(4)}`;
}

function isBuiltinProvider(provider) {
  return String(provider || "").trim().toLowerCase() === "okeybot";
}

function formatCredits(n) {
  const v = Number(n || 0);
  return `${Math.round(v)} credits`;
}

function formatUsageModelName(item, modelsContext) {
  // If the backend says it's custom and we have a name, do: "Cool Guy (gpt-4)"
  if (item.is_custom && item.custom_name) {
    return (
      <span>
        {item.custom_name} <span style={{ opacity: 0.6, fontSize: "0.85em" }}>({item.model})</span>
      </span>
    );
  }

  // Otherwise standard formatting (looks up display name from context)
  return formatModelName(item.provider, item.model, modelsContext);
}

function formatNumber(num) {
  if (num >= 1000000) {
    return `${(num / 1000000).toFixed(2)}M`;
  }
  if (num >= 1000) {
    return `${(num / 1000).toFixed(2)}K`;
  }
  return num.toString();
}

function getLastMonths(count = 12) {
  const months = [];
  const now = new Date();

  for (let i = 0; i < count; i++) {
    const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
    months.push({
      value: `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`,
      label: date.toLocaleDateString("en-US", { month: "long", year: "numeric" })
    });
  }

  return months;
}

export default function UsagePage() {
  const {
    showToast,
    selectedMonth,
    setSelectedMonth,
    fetchMonthUsage,
    monthlyUsageData,
    trackEvent,
    models,
    fetchModels
  } = useApp();

  const [usageData, setUsageData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  const monthOptions = getLastMonths(12);
  const selectedLabel = monthOptions.find(m => m.value === selectedMonth)?.label || "";

  useEffect(() => {
    fetchMonthUsage(selectedMonth);

  }, [selectedMonth, fetchMonthUsage]);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Keep local usageData in sync with global monthlyUsageData for this page
  useEffect(() => {
    if (monthlyUsageData) {
      setUsageData(monthlyUsageData);
      setLoading(false);
    }
  }, [monthlyUsageData]);

  // Ensure models are loaded to resolve display names
  useEffect(() => {
    if (!models) {
      fetchModels();
    }
  }, [models, fetchModels]);

  function selectMonth(value) {
    setSelectedMonth(value);
    setDropdownOpen(false);
    trackEvent("usage_history_view", { month: value });
  }

  const providerBreakdown = usageData?.current_month?.model_breakdown
    ? groupByProvider(usageData.current_month.model_breakdown)
    : {};

  const providerEntries = Object.entries(providerBreakdown).sort((a, b) => {
    const [pa, da] = a;
    const [pb, db] = b;
    const aBuiltin = isBuiltinProvider(pa);
    const bBuiltin = isBuiltinProvider(pb);
    // API spend first, built-in (credits) last
    if (aBuiltin !== bBuiltin) return aBuiltin ? 1 : -1;
    // Within group: sort by the correct unit
    if (aBuiltin) return (db.credits || 0) - (da.credits || 0);
    return (db.cost || 0) - (da.cost || 0);
  });

  return (
    <>
      {loading && !usageData ? (
        <div className="spinner" />
      ) : (
        <>
          <div className="card">
            <div className="card-header">
              <svg className="card-icon" fill="currentColor" viewBox="0 0 24 24">
                <path d="M3 13h2v-2H3v2zm0 4h2v-2H3v2zm0-8h2V7H3v2zm4 4h14v-2H7v2zm0 4h14v-2H7v2zM7 7v2h14V7H7z" />
              </svg>
              <h2 style={{ flex: 1 }}>Usage Statistics</h2>

              {/* Custom Dropdown */}
              <div style={{ position: "relative" }} ref={dropdownRef}>
                <button
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                  disabled={loading}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.5rem",
                    padding: "0.5rem 0.75rem",
                    borderRadius: "8px",
                    border: "1px solid var(--border-color)",
                    background: "var(--bg-secondary)",
                    color: "var(--text-primary)",
                    fontSize: "0.875rem",
                    fontWeight: "500",
                    cursor: loading ? "not-allowed" : "pointer",
                    transition: "all 0.2s ease",
                    opacity: loading ? 0.5 : 1
                  }}
                  className="month-selector-button"
                >
                  <svg width={16} height={16} fill="currentColor" viewBox="0 0 24 24">
                    <path d="M9 11H7v2h2v-2zm4 0h-2v2h2v-2zm4 0h-2v2h2v-2zm2-7h-1V2h-2v2H8V2H6v2H5c-1.11 0-1.99.9-1.99 2L3 20c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 16H5V9h14v11z" />
                  </svg>
                  <span>{selectedLabel}</span>
                  <svg
                    width={16}
                    height={16}
                    fill="currentColor"
                    viewBox="0 0 24 24"
                    style={{
                      transition: "transform 0.2s ease",
                      transform: dropdownOpen ? "rotate(180deg)" : "rotate(0deg)"
                    }}
                  >
                    <path d="M7 10l5 5 5-5z" />
                  </svg>
                </button>

                {dropdownOpen && (
                  <div
                    style={{
                      position: "absolute",
                      top: "calc(100% + 0.5rem)",
                      right: 0,
                      background: "var(--bg-secondary)",
                      border: "1px solid var(--border-color)",
                      borderRadius: "8px",
                      boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                      zIndex: 1000,
                      minWidth: "200px",
                      maxHeight: "300px",
                      overflowY: "auto",
                      animation: "slideDown 0.2s ease-out"
                    }}
                  >
                    {monthOptions.map((month) => (
                      <button
                        key={month.value}
                        onClick={() => selectMonth(month.value)}
                        style={{
                          width: "100%",
                          padding: "0.75rem 1rem",
                          textAlign: "left",
                          border: "none",
                          background: selectedMonth === month.value
                            ? "rgba(88, 101, 242, 0.1)"
                            : "transparent",
                          color: selectedMonth === month.value
                            ? "var(--accent)"
                            : "var(--text-primary)",
                          fontSize: "0.875rem",
                          fontWeight: selectedMonth === month.value ? "600" : "400",
                          cursor: "pointer",
                          transition: "all 0.15s ease",
                          borderBottom: "1px solid var(--border-color)"
                        }}
                        onMouseEnter={(e) => {
                          if (selectedMonth !== month.value) {
                            e.target.style.background = "var(--bg-primary)";
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (selectedMonth !== month.value) {
                            e.target.style.background = "transparent";
                          }
                        }}
                      >
                        {month.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="stats-grid" style={{ marginTop: "1rem" }}>
              <div className="stat-card">
                <div className="stat-label">Total Tokens</div>
                <div className="stat-value" style={{ color: "#3b82f6" }}>
                  {loading ? (
                    <div style={{
                      background: 'var(--bg-secondary)',
                      height: '32px',
                      width: '80px',
                      borderRadius: '4px',
                      animation: 'pulse 1.5s ease-in-out infinite',
                      margin: '0 auto'
                    }} />
                  ) : (
                    formatNumber(usageData?.current_month?.total_tokens || 0)
                  )}
                </div>
                <div className="stat-help">Total tokens used</div>
              </div>
              <div className="stat-card">
                <div className="stat-label">API Requests</div>
                <div className="stat-value" style={{ color: "#10b981" }}>
                  {loading ? (
                    <div style={{
                      background: 'var(--bg-secondary)',
                      height: '32px',
                      width: '60px',
                      borderRadius: '4px',
                      animation: 'pulse 1.5s ease-in-out infinite',
                      margin: '0 auto'
                    }} />
                  ) : (
                    usageData?.current_month?.total_requests || 0
                  )}
                </div>
                <div className="stat-help">API calls made</div>
              </div>
            </div>
          </div>

          {providerEntries.length > 0 && (
            <div className="card">
              <div className="card-header">
                <svg className="card-icon" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
                </svg>
                <h2>By Provider</h2>
              </div>

              <div style={{ marginTop: "1rem" }}>
                {providerEntries.map(([provider, data], idx) => (
                  <div
                    key={idx}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      padding: "1rem",
                      background: "var(--bg-secondary)",
                      borderRadius: "8px",
                      marginBottom: "0.75rem",
                      border: "1px solid var(--border-color)"
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <div
                        style={{
                          fontWeight: "500",
                          color: "var(--text-primary)",
                          marginBottom: "0.25rem"
                        }}
                      >
                        {formatProviderName(provider)}
                      </div>
                      <div style={{ fontSize: "0.875rem", color: "var(--text-muted)" }}>
                        {data.requests} requests • {formatNumber(data.tokens)} tokens
                      </div>
                    </div>
                    <div style={{ textAlign: "right" }}>
                      <div
                        style={{
                          fontSize: "1.25rem",
                          fontWeight: "600",
                          color: "#f59e0b"
                        }}
                      >
                        {isBuiltinProvider(provider) ? formatCredits(data.credits) : formatCost(data.cost)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {usageData?.current_month?.model_breakdown &&
            usageData.current_month.model_breakdown.length > 0 && (
              <div className="card">
                <div className="card-header">
                  <svg className="card-icon" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zM9 17H7v-7h2v7zm4 0h-2V7h2v10zm4 0h-2v-4h2v4z" />
                  </svg>
                  <h2>By Model</h2>
                </div>
                <div style={{ marginTop: "1rem", width: "100%", overflowX: "auto" }}>
                  <table className="usage-table">
                    <thead>
                      <tr>
                        <th className="usage-header align-left col-model">
                          Model
                        </th>
                        <th className="usage-header usage-col-requests">
                          Requests
                        </th>
                        <th className="usage-header usage-col-tokens">
                          Tokens
                        </th>
                        <th className="usage-header">
                          Spending
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {usageData.current_month.model_breakdown
                        .slice()
                        .sort((a, b) => {
                          const aBuiltin = isBuiltinProvider(a.provider);
                          const bBuiltin = isBuiltinProvider(b.provider);
                          // API spend first, built-in (credits) last
                          if (aBuiltin !== bBuiltin) return aBuiltin ? 1 : -1;
                          // Within group: sort by the correct unit
                          if (aBuiltin) {
                            return Number(b.credits || 0) - Number(a.credits || 0);
                          }
                          return Number(b.cost || 0) - Number(a.cost || 0);
                        })
                        .map((model, idx) => (
                          <tr key={idx} className="usage-row">
                            <td className="usage-cell align-left">
                              <div className="usage-model-name">
                                {formatUsageModelName(model, models)}
                              </div>
                              {/* Mobile-only stats subtitle */}
                              <div className="usage-mobile-meta">
                                {model.requests} req • {formatNumber(model.input_tokens + model.cached_tokens + model.output_tokens)} tok
                              </div>
                            </td>
                            <td className="usage-cell usage-cell-muted usage-col-requests" style={{ textAlign: "right" }}>
                              {model.requests}
                            </td>
                            <td className="usage-cell usage-cell-muted usage-col-tokens" style={{ textAlign: "right" }}>
                              {formatNumber(
                                model.input_tokens + model.cached_tokens + model.output_tokens
                              )}
                            </td>
                            <td className="usage-cell usage-cost" style={{ textAlign: "right" }}>
                              {isBuiltinProvider(model.provider) ? formatCredits(model.credits) : formatCost(model.cost)}
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

          <div className="card">
            <div
              style={{
                background: "var(--bg-primary)",
                padding: "1rem",
                borderRadius: "8px",
                border: "1px solid var(--border-color)"
              }}
            >
              <div style={{ display: "flex", alignItems: "start", gap: "0.75rem" }}>
                <svg
                  width={20}
                  height={20}
                  fill="var(--accent)"
                  viewBox="0 0 24 24"
                  style={{ flexShrink: 0, marginTop: "2px" }}
                >
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
                </svg>
                <div
                  style={{
                    fontSize: "0.875rem",
                    color: "var(--text-muted)",
                    lineHeight: "1.5"
                  }}
                >
                  <strong style={{ color: "var(--text-secondary)" }}>Note:</strong>{" "}
                  Costs are based on actual API token usage and current pricing, but always rely on your provider's official dashboard for your final billing.
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
}
